/**
 * ProductsSidebar V2
 *
 * Sidebar-Navigation für den Products-Bereich (PIM).
 * Style: Matched with Workspace sidebar for consistency.
 *
 * Features:
 * - Navigation zu allen PIM-Bereichen
 * - Active State Indicator
 * - Responsive: Collapsed auf Mobile
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Package,
  Tag,
  FolderTree,
  Layers,
  BookOpen,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/v2/ui';

interface MenuItem {
  id: string;
  name: string;
  icon: React.ElementType;
  path: string;
  disabled?: boolean;
  badge?: string;
}

const MENU_ITEMS: MenuItem[] = [
  {
    id: 'products',
    name: 'Produkte',
    icon: Package,
    path: '/v2/products',
  },
  {
    id: 'brands',
    name: 'Brands',
    icon: Tag,
    path: '/v2/products/brands',
  },
  {
    id: 'categories',
    name: 'Kategorien',
    icon: FolderTree,
    path: '/v2/products/categories',
  },
  {
    id: 'issues',
    name: 'Issues',
    icon: AlertTriangle,
    path: '/v2/products/issues',
  },
  {
    id: 'attributes',
    name: 'Attribute',
    icon: Layers,
    path: '/v2/products/attributes',
    disabled: true,
    badge: 'Soon',
  },
  {
    id: 'families',
    name: 'Familien',
    icon: BookOpen,
    path: '/v2/products/families',
    disabled: true,
    badge: 'Soon',
  },
];

export interface ProductsSidebarProps {
  className?: string;
}

export function ProductsSidebar({ className }: ProductsSidebarProps) {
  return (
    <aside
      className={cn(
        'w-64 bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 flex-shrink-0 shadow-sm',
        className
      )}
    >
      <div className="sticky top-16 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 px-3 py-4 mb-4">
          <div className="p-2 rounded-xl bg-primary-100 dark:bg-primary-900/30">
            <Package className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <span className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            PIM
          </span>
        </div>

        {/* Navigation */}
        <nav className="space-y-1" aria-label="Products Navigation">
          {MENU_ITEMS.map((item) => (
            <SidebarItem key={item.id} item={item} />
          ))}
        </nav>
      </div>
    </aside>
  );
}

interface SidebarItemProps {
  item: MenuItem;
}

function SidebarItem({ item }: SidebarItemProps) {
  const Icon = item.icon;
  const navigate = useNavigate();
  const location = useLocation();

  // Base classes matching Workspace style
  const baseClasses = cn(
    'flex items-center gap-3 px-3 py-2.5 rounded-lg',
    'text-sm font-semibold transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-primary-500'
  );

  // Handle click on active nav item to force page reload
  const handleNavClick = (e: React.MouseEvent) => {
    const isCurrentlyActive = location.pathname === item.path;

    if (isCurrentlyActive) {
      e.preventDefault();
      // Force reload by navigating with replace and reload state
      navigate(item.path, { replace: true, state: { reload: Date.now() } });
    }
  };

  // Disabled state
  if (item.disabled) {
    return (
      <div className={cn(baseClasses, 'text-neutral-400 dark:text-neutral-600 cursor-not-allowed')}>
        <Icon className="h-5 w-5" />
        <span className="flex-1 truncate">{item.name}</span>
        {item.badge && (
          <Badge variant="secondary" size="sm">
            {item.badge}
          </Badge>
        )}
      </div>
    );
  }

  // Active NavLink - matches Workspace style
  return (
    <NavLink
      to={item.path}
      onClick={handleNavClick}
      end={item.id === 'products'} // Exact match for products list
      className={({ isActive }) =>
        cn(
          baseClasses,
          isActive
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
            : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800'
        )
      }
    >
      <Icon className="h-5 w-5" />
      <span className="flex-1 truncate">{item.name}</span>
      {item.badge && (
        <Badge variant="secondary" size="sm">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );
}

export default ProductsSidebar;
